// Playback order for a multi-video footage batch.
//
// Brinno (and every other long-shoot timelapse camera) splits one continuous
// shoot into many files — daily rollover, or a 500 MB/2 GB chunk limit — named
// TLC00001.AVI, TLC00002.AVI, … The order those clips must be concatenated in
// is the order they were RECORDED, and getting it wrong silently produces a
// scrambled timelapse, so this module is the one place that decides it.
//
// Rule, in order of preference:
//   1. capture time (File.lastModified) ascending — the truth when the files
//      still carry their original mtimes, which is the normal case for a card
//      copied straight off the camera.
//   2. natural sort of the file name — the fallback when mtimes are missing,
//      identical, or clearly untrustworthy (a bulk copy that re-stamped every
//      file to the same second, which is exactly what an SD-card copy tool or
//      a cloud-drive round trip does). "Natural" means TLC00009 < TLC00010,
//      not the lexicographic TLC00010 < TLC00009.
//
// The user can always drag to override; this only picks the default.

// Compare two names the way a human reads them: digit runs compare
// numerically, everything else compares case-insensitively as text.
export function naturalCompare(a, b) {
  const left = String(a ?? '');
  const right = String(b ?? '');
  const chunk = /(\d+)|(\D+)/g;
  const leftParts = left.toLowerCase().match(chunk) || [];
  const rightParts = right.toLowerCase().match(chunk) || [];
  const len = Math.min(leftParts.length, rightParts.length);
  for (let i = 0; i < len; i += 1) {
    const lp = leftParts[i];
    const rp = rightParts[i];
    const lNum = /^\d/.test(lp);
    const rNum = /^\d/.test(rp);
    if (lNum && rNum) {
      const diff = Number(lp) - Number(rp);
      if (diff !== 0) return diff < 0 ? -1 : 1;
      // Equal numerically (e.g. "007" vs "7") — keep going; the tie is broken
      // by later chunks, then by raw length below.
    } else if (lp !== rp) {
      return lp < rp ? -1 : 1;
    }
  }
  if (leftParts.length !== rightParts.length) {
    return leftParts.length < rightParts.length ? -1 : 1;
  }
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

// True when capture times can't order this batch: any entry missing a time, or
// every entry sharing one time (a bulk copy that re-stamped them all).
export function captureTimesAreUnusable(entries) {
  const list = entries || [];
  if (list.length < 2) return list.some((e) => !Number.isFinite(e?.capturedAt));
  const times = [];
  for (const entry of list) {
    if (!Number.isFinite(entry?.capturedAt)) return true;
    times.push(Math.floor(entry.capturedAt / 1000));
  }
  return new Set(times).size === 1;
}

// The default playback order for a scanned batch. Pure — returns a new array
// and never mutates the input.
export function defaultVideoOrder(entries) {
  const list = [...(entries || [])];
  const byName = captureTimesAreUnusable(list);
  list.sort((a, b) => {
    if (!byName) {
      const diff = (a.capturedAt || 0) - (b.capturedAt || 0);
      if (diff !== 0) return diff < 0 ? -1 : 1;
    }
    return naturalCompare(a.fileName, b.fileName);
  });
  return list;
}

// Which rule the default order used, so the preflight dialog can say so
// ("ordered by capture time" vs "ordered by file name").
export function orderingBasis(entries) {
  return captureTimesAreUnusable(entries) ? 'fileName' : 'capturedAt';
}

// Move one item from `fromIndex` to `toIndex`. Pure; out-of-range indices are
// a no-op (returns a copy) so a stray drag event can't corrupt the list.
export function reorder(list, fromIndex, toIndex) {
  const next = [...(list || [])];
  if (
    !Number.isInteger(fromIndex) || !Number.isInteger(toIndex)
    || fromIndex < 0 || fromIndex >= next.length
    || toIndex < 0 || toIndex >= next.length
    || fromIndex === toIndex
  ) {
    return next;
  }
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

// Stamp 0-based `order` onto each entry, which is what the trigger persists and
// what a later concat pass reads. Applied after every reorder so the field and
// the visible list can never disagree.
export function withOrder(list) {
  return (list || []).map((entry, index) => ({ ...entry, order: index }));
}

// The `order` to hand the next clip appended to a project — one past the
// highest order already stored.
//
// MAX + 1, never the COUNT of existing docs. A count is only a valid base while
// the stored orders are contiguous, and a partially-failed batch is exactly
// when they are not: upload 10 clips, lose the ones at 6 and 9, and the project
// holds orders {0,1,2,3,4,5,7,8}. Re-dropping the folder in a NEW session (the
// resume path the dialog advertises) re-stamps the 2 survivors 0 and 1, and a
// count-based base of 8 writes them as 8 and 9 — order 8 twice, and the clip
// recorded 7th sorting second-to-last. There is no error when that happens,
// just a silently scrambled timelapse.
export function nextOrderBase(videos) {
  let max = -1;
  for (const video of videos || []) {
    const order = Number(video?.order);
    if (Number.isFinite(order) && order > max) max = order;
  }
  return max + 1;
}
